import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Play, MessageCircle, X, Maximize2 } from "lucide-react";
import { YOUTUBE_TESTIMONIALS, CONTACT, TESTIMONIES_IMAGE } from "../lib/constants";

const ease = [0.76, 0, 0.24, 1];

export default function Testimonials() {
  const [zoom, setZoom] = useState(false);
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 lg:py-40 bg-cream border-t border-ink-15"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
        <div className="max-w-5xl mb-14 md:mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease }}
            className="font-display font-light text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-[-0.03em] text-ink"
            style={{ fontVariationSettings: '"SOFT" 60' }}
          >
            The proof isn't in a brochure. <span className="italic text-saffron">It's in their words.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease }}
            className="mt-8 text-lg md:text-xl text-ink/75 max-w-3xl font-light leading-relaxed"
          >
            Unscripted testimonies from actual NavSankalpIAS students. Tap any card to watch on YouTube.
          </motion.p>
        </div>

        {/* Video grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {YOUTUBE_TESTIMONIALS.map((v, idx) => (
            <motion.a
              key={v.id}
              href={`https://www.youtube.com/watch?v=${v.id}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`testimonial-${idx}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (idx % 3) * 0.09, ease }}
              className="group block relative overflow-hidden bg-cream-warm border border-ink-15 hover:border-saffron/60 transition-colors duration-500"
            >
              <div className="relative overflow-hidden" style={{ aspectRatio: "16/9" }}>
                <img
                  src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`}
                  alt={v.label}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-full bg-saffron flex items-center justify-center play-pulse transition-transform duration-500 group-hover:scale-110">
                    <Play size={26} strokeWidth={1.5} className="text-cream translate-x-[2px]" fill="currentColor" />
                  </div>
                </div>
                <div className="absolute top-3 left-3 text-[10px] tracking-[0.28em] uppercase font-semibold text-cream bg-ink/80 border border-cream/25 px-2 py-1">
                  YouTube · {String(idx + 1).padStart(2, "0")}
                </div>
              </div>
              <div className="p-5 md:p-6 flex items-center justify-between border-t border-ink-15">
                <span
                  className="font-display italic text-ink text-base md:text-lg"
                  style={{ fontVariationSettings: '"SOFT" 80' }}
                >
                  {v.label}
                </span>
                <span className="text-[10px] tracking-[0.28em] uppercase text-ink/50 group-hover:text-saffron transition-colors font-medium">
                  Watch →
                </span>
              </div>
            </motion.a>
          ))}

          {/* Trailing note card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2, ease }}
            className="p-8 md:p-10 bg-saffron text-cream flex flex-col justify-between min-h-[280px]"
            data-testid="testimonials-note"
          >
            <div>
              <p
                className="font-display text-2xl md:text-3xl italic leading-snug font-light"
                style={{ fontVariationSettings: '"SOFT" 100' }}
              >
                We measure mentorship not by promises — but by aspirants who quietly, steadily, keep showing up.
              </p>
            </div>
            <div className="mt-8 pt-6 border-t border-cream/30 flex items-center justify-between">
              <span className="text-[10px] tracking-[0.28em] uppercase text-cream/80 font-semibold">NavSankalpIAS</span>
              <a
                href={CONTACT.youtube}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="testimonials-yt-channel"
                className="text-[10px] tracking-[0.28em] uppercase text-cream underline underline-offset-4 decoration-1 font-semibold"
              >
                Full Channel →
              </a>
            </div>
          </motion.div>
        </div>

        {/* ============ DIRECT MESSAGES FROM STUDENTS ============ */}
        <div className="mt-20 md:mt-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease }}
            className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-8 md:mb-10"
          >
            <div className="col-span-12 lg:col-span-8">
              <div className="inline-flex items-center gap-2 mb-5">
                <MessageCircle size={14} className="text-saffron" strokeWidth={2} />
                <span className="text-[10px] tracking-[0.32em] uppercase text-saffron font-semibold">
                  Direct from Students · WhatsApp
                </span>
              </div>
              <h3
                className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-ink"
                style={{ fontVariationSettings: '"SOFT" 60' }}
              >
                Messages that reached Sir <span className="italic text-saffron">unprompted.</span>
              </h3>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <p className="text-base md:text-lg text-ink/70 font-light leading-relaxed">
                Screenshots from actual student chats — identities redacted. This is the kind of trust NavSankalp mentorship builds.
              </p>
            </div>
          </motion.div>

          <motion.button
            onClick={() => setZoom(true)}
            data-testid="testimonies-image"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease }}
            className="group relative block w-full rounded-3xl overflow-hidden border border-ink-15 bg-cream-warm text-left"
            aria-label="Zoom student messages"
          >
            <div className="relative">
              <img
                src={TESTIMONIES_IMAGE}
                alt="Student messages to NavSankalpIAS mentor"
                loading="lazy"
                className="w-full h-auto object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.01]"
              />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-ink/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute top-4 left-4 text-[10px] tracking-[0.28em] uppercase font-semibold text-cream bg-ink/80 border border-cream/25 px-3 py-1.5 rounded-full">
                Redacted · Real Chats
              </div>
              <div className="absolute bottom-4 right-4 flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-semibold text-ink bg-saffron px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <Maximize2 size={12} strokeWidth={2} /> Zoom
              </div>
            </div>
          </motion.button>
        </div>

        <AnimatePresence>
          {zoom && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[150] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
              onClick={() => setZoom(false)}
              data-testid="testimonies-lightbox"
            >
              <button
                onClick={() => setZoom(false)}
                className="absolute top-6 right-6 md:top-8 md:right-8 w-12 h-12 border border-white/40 flex items-center justify-center text-white hover:text-saffron hover:border-saffron transition-colors rounded-full"
                aria-label="Close"
                data-testid="testimonies-lightbox-close"
              >
                <X size={20} />
              </button>
              <motion.img
                initial={{ scale: 0.94, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.94, opacity: 0 }}
                transition={{ duration: 0.4, ease }}
                src={TESTIMONIES_IMAGE}
                alt="Student messages full-view"
                className="max-w-full max-h-[92vh] object-contain shadow-2xl rounded-xl"
                onClick={(e) => e.stopPropagation()}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
